import { X } from 'lucide-react'

interface DynListProps {
  /** Stable key for the collection — used as the form data attribute. */
  name: string
  /** Current rows. The shape is whatever the caller stores. */
  rows: Record<string, string>[]
  /** Column definitions. Drives the row inputs left-to-right. */
  cols: Array<{ key: string; placeholder?: string; type?: 'text' | 'select'; options?: string[] }>
  /** "Add" button label (e.g. "+ Add KPI"). */
  addLabel: string
  onChange: (rows: Record<string, string>[]) => void
}

/**
 * Repeating-row collection editor. Mirrors `<div class="dyn-list">` +
 * `<div class="dyn-item cols-N">` + `<div class="dyn-add">` from the
 * original lorenza.html. Used by KPIs, stakeholders, assumptions,
 * personas, use cases, data sources, integrations, risks, milestones.
 *
 * CSS for `.dyn-list`, `.dyn-item`, `.dyn-add`, `.dyn-remove` is already
 * in `styles/briefer.css`.
 */
export function DynList({ name, rows, cols, addLabel, onChange }: DynListProps) {
  function addRow() {
    onChange([...rows, Object.fromEntries(cols.map((c) => [c.key, '']))])
  }
  function removeRow(idx: number) {
    onChange(rows.filter((_, i) => i !== idx))
  }
  function setCell(idx: number, key: string, value: string) {
    onChange(rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row)))
  }
  return (
    <div className={`dyn-list cols-${cols.length}`} data-dyn={name}>
      {rows.map((row, idx) => (
        <div key={idx} className={`dyn-item cols-${cols.length}`}>
          {cols.map((col) => (
            <input
              key={col.key}
              type="text"
              className="dyn-item-input"
              // Contextus widget reads `data-dyn-key` (cell) inside `[data-dyn]`
              // (list) to offer per-cell row suggestions with sibling context.
              data-dyn-key={col.key}
              placeholder={col.placeholder ?? ''}
              value={row[col.key] ?? ''}
              onChange={(e) => setCell(idx, col.key, e.target.value)}
            />
          ))}
          <button
            type="button"
            className="dyn-remove"
            aria-label="Remove row"
            onClick={() => removeRow(idx)}
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button type="button" className="dyn-add" onClick={addRow}>
        {addLabel}
      </button>
    </div>
  )
}
